import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Btn } from "../BrandUI";
import chevronRight from "../../../../assets/brand/nav/chevron-right-20.svg";

// Phone building blocks shared by Athlete Mode, Coach Mode and CacheShare
// (Figma ATHLETE MODE V2 / COACH MODE V2 mobile). Presentational only, bm
// tokens only. Phone trees pick these in JS; desktop keeps BrandUI.

const cx = (...parts) => parts.filter(Boolean).join(" ");

// Text columns carry `break-words` (overflow-wrap is inherited): a long
// unbroken value such as an email, a URL or a code wraps inside its card
// instead of pushing the page sideways on a 320px screen. Normal copy
// renders exactly as without it.

// A valueClass that sets its own text colour replaces the default ink.
const TEXT_COLOUR = /(^|\s)!?text-(bm-|white|black|\[#)/;
const withInk = (valueClass) => (valueClass && TEXT_COLOUR.test(valueClass) ? valueClass : cx("text-bm-ink", valueClass));

// A stable key for a row: its own key, else a string label, else the index.
const rowKey = (row, i) => row.key ?? (typeof row.label === "string" ? row.label : i);

// ------------------------------------------------------------------ Cards
const CARD_DENSITY = {
  default: "py-4 flex flex-col gap-3",
  list: "py-1",
  stat: "py-[6px]",
};

// A card rendered as a list (`as="ul"` / `as="ol"`) gets role="list" unless
// the caller sets a role: the reset drops its bullets, and Safari with
// VoiceOver then drops the list role ("list, N items") too.
const LIST_TAGS = new Set(["ul", "ol"]);

// Card (2022:1643, 2019:1725, 2019:1760, subtle 2024:1574).
export const MobileCard = ({ as: Tag = "div", tone = "white", density = "default", className = "", children, ...props }) => (
  <Tag
    role={LIST_TAGS.has(Tag) ? "list" : undefined}
    className={cx(
      "rounded-[16px] border border-bm-mutedbg px-[18px]",
      CARD_DENSITY[density] || CARD_DENSITY.default,
      tone === "subtle" ? "bg-bm-subtle" : "bg-white",
      className,
    )}
    {...props}
  >
    {children}
  </Tag>
);

// Card head (2019:1725): title and a small sub, with an optional node on the
// right (a badge or an avatar).
export const MobileCardHead = ({ title, sub, right, titleAs: Title = "h2", className = "" }) => (
  <div className={cx("flex items-center gap-3", className)}>
    <div className="min-w-0 flex-1 flex flex-col gap-[2px] break-words">
      <Title className="bm-h-s text-bm-ink">{title}</Title>
      {sub && <p className="bm-body-xs text-bm-muted">{sub}</p>}
    </div>
    {right}
  </div>
);

// Page head on phone tab and flow pages (2019:1694, 2022:1735, 2023:1542,
// 2407:889). `children` are the header actions, stacked full width.
export const MobilePageHead = ({ eyebrow, eyebrowBrand = false, title, sub, subSize = "s", className = "", children }) => (
  <div className={cx("flex flex-col gap-[18px] break-words", className)}>
    <div className={cx("flex flex-col", !eyebrow && subSize !== "m" ? "gap-1" : "gap-[6px]")}>
      {eyebrow && <p className={cx("bm-overline", eyebrowBrand && "!text-bm-orange")}>{eyebrow}</p>}
      <h1 className="bm-h-l text-bm-ink">{title}</h1>
      {sub && <p className={cx(subSize === "m" ? "bm-body-m" : "bm-body-s", "text-bm-muted")}>{sub}</p>}
    </div>
    {children && <div className="flex flex-col gap-3">{children}</div>}
  </div>
);

// A KPI value never breaks across lines ("$1,234,5" / "67" in a half-width
// tile at 320 to 360px). When it is wider than its tile it steps down one
// type size, Num/ML 22 to Num/M 17, and if that is still too wide its font
// scales down until it fits. Measured before paint, from the value's own
// overflow, and again when the tile's width or the web fonts change. jsdom
// has no layout, so tests see Num/ML.
const KPI_FULL = { key: "", step: "ml", px: null };
const KPI_M_PX = 17;
const KPI_MIN_PX = 10;

const useKpiFit = (value) => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  const [fontsTick, setFontsTick] = useState(0);
  const [fit, setFit] = useState(KPI_FULL);
  // A new value, tile width or font load starts again from Num/ML.
  const valueKey = typeof value === "string" || typeof value === "number" ? String(value) : "";
  const key = `${valueKey}|${width}|${fontsTick}`;
  const current = fit.key === key ? fit : { ...KPI_FULL, key };

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollWidth, clientWidth } = el;
    if (!clientWidth || scrollWidth <= clientWidth) return;
    if (current.step === "ml") {
      setFit({ key, step: "m", px: null });
      return;
    }
    const px = current.px || KPI_M_PX;
    const next = Math.max(KPI_MIN_PX, Math.floor((px * clientWidth) / scrollWidth));
    if (next < px) setFit({ key, step: "m", px: next });
  }, [key, current.step, current.px]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver !== "function") return undefined;
    const observer = new ResizeObserver(() => setWidth(el.clientWidth));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let live = true;
    const ready = typeof document !== "undefined" ? document.fonts?.ready : null;
    if (ready && typeof ready.then === "function") ready.then(() => live && setFontsTick(1));
    return () => {
      live = false;
    };
  }, []);

  return {
    ref,
    className: current.step === "ml" ? "bm-num-ml" : "bm-num-m",
    style: current.px ? { fontSize: `${current.px}px` } : undefined,
  };
};

// KPI tile (2019:1699, coach 2406:827 / 2406:831). The label and the sub
// wrap; the value stays on one line (see useKpiFit).
export const MobileKpiTile = ({ label, value, sub, muted = false, className = "" }) => {
  const fitValue = useKpiFit(value);
  return (
    <div
      className={cx(
        "flex min-w-0 flex-col gap-1 break-words rounded-[16px] border border-bm-mutedbg p-[14px]",
        muted ? "bg-bm-mutedbg" : "bg-white",
        className,
      )}
    >
      <p className="bm-overline">{label}</p>
      <div ref={fitValue.ref} className={cx(fitValue.className, "whitespace-nowrap text-bm-ink")} style={fitValue.style}>
        {value}
      </div>
      {sub && <div className="bm-body-xs text-bm-muted">{sub}</div>}
    </div>
  );
};

// Stat rows in a card (2019:1760, 2406:863, 2415:1555): overline label and a
// sub on the left, the number on the right, 1px rules between rows.
export const StatList = ({ rows = [], className = "", ...props }) => (
  <MobileCard density="stat" className={className} {...props}>
    {rows.map((row, i) => (
      <React.Fragment key={rowKey(row, i)}>
        {i > 0 && <ListRule />}
        <div className="flex items-center gap-3 py-3">
          <div className="flex-1 min-w-0 flex flex-col gap-[2px] break-words">
            <p className="bm-overline">{row.label}</p>
            {row.sub && <p className="bm-body-xs text-bm-muted">{row.sub}</p>}
          </div>
          <div className={cx("bm-num-m whitespace-nowrap", withInk(row.valueClass))}>{row.value}</div>
        </div>
      </React.Fragment>
    ))}
  </MobileCard>
);

// Label / value row (2023:1550, 2472:3345). Long values wrap instead of
// clipping; `stack` puts the value under the label (long ids, emails).
export const KeyValueRow = ({ label, value, emphasis = false, stack = false, last = false, className = "" }) => (
  <div
    className={cx(
      stack ? "flex flex-col gap-1 py-[11px]" : "flex items-center justify-between gap-3 py-[11px]",
      !last && "border-b border-bm-mutedbg",
      className,
    )}
  >
    <span className={cx("bm-body-m text-bm-muted", !stack && "max-w-[60%] shrink-0")}>{label}</span>
    {stack ? (
      <span className="bm-body-s text-bm-ink break-all">{value}</span>
    ) : (
      <span className={cx("min-w-0 text-right break-words text-bm-ink", emphasis ? "bm-num-m" : "bm-label-l")}>{value}</span>
    )}
  </div>
);

// Meta columns on a card (2022:1653, 2412:1498).
export const MetaCols = ({ items = [], className = "" }) => (
  <div className={cx("flex gap-3", className)}>
    {items.map((item, i) => (
      <div key={rowKey(item, i)} className="min-w-0 flex-1 flex flex-col gap-[2px] break-words">
        <p className="bm-overline">{item.label}</p>
        <div className={item.valueClass || "bm-label-l text-bm-ink"}>{item.value}</div>
      </div>
    ))}
  </div>
);

// Text action with a trailing arrow (2022:1660, 2025:1549). The arrow is
// hidden from assistive tech, so the accessible name is the label alone.
// `to` renders a router Link, `href` an anchor, else a button (`busy`
// disables it; callers swap the label, e.g. "Opening...").
export const TextLink = ({
  to,
  href,
  onClick,
  tone = "brand",
  arrow = true,
  busy = false,
  disabled,
  className = "",
  children,
  ...props
}) => {
  const classes = cx(
    "inline-flex items-center bm-label-l disabled:cursor-not-allowed disabled:opacity-60",
    tone === "muted" ? "text-bm-muted" : "text-bm-orange",
    className,
  );
  const inner = (
    <>
      {children}
      {arrow && (
        <span aria-hidden="true" className="ml-2">
          →
        </span>
      )}
    </>
  );
  if (to) {
    return (
      <Link to={to} onClick={onClick} className={classes} {...props}>
        {inner}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} onClick={onClick} className={classes} {...props}>
        {inner}
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={Boolean(busy || disabled)}
      aria-busy={busy || undefined}
      className={classes}
      {...props}
    >
      {inner}
    </button>
  );
};

// Filter chips that scroll sideways to the screen edge (2022:1631, 2407:935).
// Children are BrandUI Chips. Not for chips that open a popover (the
// overflow would clip it).
export const ChipScroller = ({ className = "", children, ...props }) => (
  <div className={cx("-mx-6 flex gap-2 overflow-x-auto px-6 bm-no-scrollbar [&>*]:shrink-0", className)} {...props}>
    {children}
  </div>
);

export const ListRule = ({ className = "" }) => <div aria-hidden="true" className={cx("h-px w-full bg-bm-mutedbg", className)} />;

// Full-width band under the stack header (2008:1301): the deal or campaign,
// a badge, a meta line, and any extra lines (a thread link) as children.
export const ContextStrip = ({ title, badge, meta, className = "", children }) => (
  <div className={cx("flex flex-col gap-1 break-words bg-bm-subtle px-6 py-[14px]", className)}>
    {(title || badge) && (
      <div className="flex items-center gap-2">
        <p className="bm-h-xs text-bm-ink flex-1 min-w-0">{title}</p>
        {badge}
      </div>
    )}
    {meta && <p className="bm-body-xs text-bm-muted">{meta}</p>}
    {children}
  </div>
);

// Bottom action bar of the deal flow (2008:1448, 2009:1438, 2421:2595).
// Children are `Btn size="lg" className="w-full"`, primary first. It sits in
// a `px-6` body and bleeds to the screen edge.
export const StickyActionBar = ({ gap = 10, className = "", children }) => (
  <div
    className={cx(
      "sticky bottom-0 z-20 -mx-6 mt-auto flex flex-col border-t border-bm-mutedbg bg-white px-6 pt-4 pb-[max(28px,env(safe-area-inset-bottom))]",
      gap === 12 ? "gap-3" : "gap-[10px]",
      className,
    )}
  >
    {children}
  </div>
);

// Payout row (2019:1781, 2025:1586). `badge` is a label shown in an outline
// Badge (or a ready badge node); `status` is plain muted text instead.
export const PayoutRow = ({ label, sub, amount, status, badge, onClick, to, className = "" }) => {
  const classes = cx("flex w-full items-center justify-between gap-3 py-[13px] text-left", className);
  const inner = (
    <>
      <span className="flex min-w-0 flex-col gap-[2px] break-words">
        <span className="bm-h-xs text-bm-ink">{label}</span>
        {sub && <span className="bm-body-s text-bm-muted">{sub}</span>}
      </span>
      <span className="flex shrink-0 flex-col items-end gap-1">
        <span className="bm-num-m text-bm-ink">{amount}</span>
        {badge ? (
          React.isValidElement(badge) ? badge : <Badge tone="outline">{badge}</Badge>
        ) : status ? (
          <span className="bm-body-s text-bm-muted">{status}</span>
        ) : null}
      </span>
    </>
  );
  if (to) {
    return (
      <Link to={to} className={classes}>
        {inner}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={classes}>
        {inner}
      </button>
    );
  }
  return <div className={classes}>{inner}</div>;
};

// Form field (FormField · Mobile 1976:900). The control is the child (a
// `.bm-input-lg` or `.ob-input`). With `htmlFor` the label is a real <label>
// and the hint / error get the ids `{htmlFor}-hint` / `{htmlFor}-error` for
// the control's aria-describedby. An error replaces the hint. The hint is
// muted grey like the Figma helper line (2023:1571); "Optional" stays lighter.
export const FormFieldM = ({ label, optional = false, hint, error, htmlFor, className = "", children }) => {
  const LabelTag = htmlFor ? "label" : "span";
  return (
    <div className={cx("flex min-w-0 flex-col gap-2", className)}>
      {label && (
        <div className="flex items-center gap-2">
          <LabelTag htmlFor={htmlFor} className="bm-label-l text-bm-ink">
            {label}
          </LabelTag>
          {optional && <span className="bm-body-s text-bm-tertiary">Optional</span>}
        </div>
      )}
      {children}
      {error ? (
        <p id={htmlFor ? `${htmlFor}-error` : undefined} className="bm-body-s text-bm-critical">
          {error}
        </p>
      ) : hint ? (
        <p id={htmlFor ? `${htmlFor}-hint` : undefined} className="bm-body-s text-bm-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
};

// Hub row (ProfileSectionRow · Mobile 2080:979): icon tile, title and
// support line, an optional ring (none for the Setting variant), chevron.
// `icon` is a node (the edit hub's NavIcon). Rows sit flush in a HubGroup.
// The title is the button's name and the support line (then the ring's
// value) its description, so they are never read as one run of text
// ("Account informationRomulo Coach").
export const SectionRow = ({ icon, title, support, ring, onClick, last = false, className = "", ...props }) => {
  const id = useId();
  const titleId = `${id}-title`;
  const supportId = `${id}-support`;
  const ringId = `${id}-ring`;
  const describedBy = [support && supportId, ring && ringId].filter(Boolean).join(" ") || undefined;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-labelledby={titleId}
      aria-describedby={describedBy}
      className={cx("relative flex w-full items-center gap-3 px-4 py-[14px] text-left", className)}
      {...props}
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-bm-subtle">{icon}</span>
      <span className="min-w-0 flex-1 flex flex-col gap-[2px] break-words">
        <span id={titleId} className="bm-h-s text-bm-ink">
          {title}
        </span>
        {support && (
          <span id={supportId} className="bm-body-m text-bm-muted">
            {support}
          </span>
        )}
      </span>
      {ring && (
        <span id={ringId} className="flex shrink-0 items-center">
          {ring}
        </span>
      )}
      <img src={chevronRight} alt="" className="h-5 w-5 shrink-0" />
      {!last && <span aria-hidden="true" className="absolute bottom-0 left-[68px] right-0 h-px bg-bm-mutedbg" />}
    </button>
  );
};

// A labelled group of SectionRows (2084:12661).
export const HubGroup = ({ label, className = "", children }) => (
  <div className={cx("flex flex-col gap-2", className)}>
    {label && <p className="bm-overline !text-bm-tertiary">{label}</p>}
    <div className="rounded-[16px] border border-bm-mutedbg bg-white overflow-hidden">{children}</div>
  </div>
);

const SAVE_STATUS = {
  saving: { text: "Saving your changes…", tone: "text-bm-muted" },
  dirty: { text: "Unsaved changes", tone: "text-bm-muted" },
  error: { text: "Could not save.", tone: "text-bm-critical" },
  saved: { text: "All changes saved.", tone: "text-bm-positive" },
};

// Edit-profile section footer (AutosaveBar · Mobile 2082:1032): the save
// status (any status other than saving / dirty / error reads as saved, as
// the desktop SaveStatus does) and a full-width Done. Fixed to the bottom;
// pages add `pb-[150px]` to their body so the last field clears it.
export const AutosaveBar = ({ status, error, onDone, doneLabel = "Done", busy = false }) => {
  const meta = SAVE_STATUS[status] || SAVE_STATUS.saved;
  const text = status === "error" && error ? error : meta.text;
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 flex flex-col items-center gap-[10px] border-t border-bm-mutedbg bg-white px-6 pt-[14px] pb-[calc(24px+env(safe-area-inset-bottom))] shadow-[0_-4px_6px_rgba(0,0,0,0.08)]">
      <p role="status" className={cx("bm-body-s text-center", meta.tone)}>
        {text}
      </p>
      <Btn size="lg" className="w-full" onClick={onDone} disabled={busy} aria-busy={busy || undefined}>
        {doneLabel}
      </Btn>
    </div>
  );
};

// Empty list on a phone page: the web's empty copy in a card, with an
// optional action under it.
export const MobileEmpty = ({ action, className = "", children }) => (
  <MobileCard className={cx("items-center text-center", className)}>
    <p className="py-6 bm-body-m text-bm-muted">{children}</p>
    {action}
  </MobileCard>
);
